import { useCallback, useEffect, useMemo, useState } from 'react';

import { useUser } from '@/entities/user';
import { useMatchMedia } from '@/shared/lib/hooks/use-match-media.hook';

import { useUpdateUserAgreementsMutation } from '../../../api/agreements.api';
import { LocalStorageKeys } from '../../../model/constants/agreements.constant';
import { checkStepUpdate } from '../../../model/helpers/check-step-update.helper';
import { getInitialCheckboxState } from '../../../model/helpers/get-initial-checkbox-state.helper';
import { CheckboxState } from '../../../model/types/agreements.types';
import type { SizeText } from '../../../model/types/agreements-api.interface';
import { STEPS, Steps } from '../../../model/utils/agreements.util';
import { findUpdatedSteps } from '../../helpers/find-updated-steps.helper';

export const useAgreementsLogic = (onClose: () => void) => {
	const {
		latestAcceptedTermsAndConditionsUrl,
		latestPrivacyPolicyUrl,
		isAcceptedLatestTermsAndConditions,
		isAcceptedLatestPrivacyPolicy,
		acceptedTimeTC,
		acceptedTimePP,
	} = useUser();

	const isMobile = useMatchMedia('mobile');
	const isLaptop = useMatchMedia('tabletAndLaptop');
	const [updateUserAgreements, { isLoading }] = useUpdateUserAgreementsMutation();

	const sizeText: SizeText = isMobile ? 'xs' : isLaptop ? 's' : 'm';

	const steps = useMemo(
		() =>
			STEPS(
				{
					termsAndConditionsFile: latestAcceptedTermsAndConditionsUrl,
					privacyPolicyFile: latestPrivacyPolicyUrl,
				},
				sizeText,
			),
		[latestAcceptedTermsAndConditionsUrl, latestPrivacyPolicyUrl, sizeText],
	);

	const isFirstAcceptance = !acceptedTimeTC && !acceptedTimePP;

	const initialCheckboxState = useMemo(
		() => getInitialCheckboxState(steps, isAcceptedLatestTermsAndConditions, isAcceptedLatestPrivacyPolicy),
		[steps, isAcceptedLatestTermsAndConditions, isAcceptedLatestPrivacyPolicy],
	);

	const [checkboxState, setCheckboxState] = useState<CheckboxState>(initialCheckboxState);
	const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
	const [isFullscreen, setFullscreen] = useState(false);

	const currentStep = steps[currentStepIndex];

	const isAllTrue = (array: boolean[]) => array.every(Boolean);

	const isChanged = (step: Steps) => {
		const updatedSteps = findUpdatedSteps(initialCheckboxState, checkboxState);

		return checkStepUpdate(updatedSteps, step);
	};

	const handleCheckboxChange = useCallback((stepTitle: string, checkboxIndex: number) => {
		setCheckboxState((prevState) => {
			const updated = [...prevState[stepTitle]];
			updated[checkboxIndex] = !updated[checkboxIndex];

			const newState = { ...prevState, [stepTitle]: updated };

			localStorage.setItem(LocalStorageKeys.State, JSON.stringify(newState));

			return newState;
		});
	}, []);

	const nextStep = () => {
		if (currentStepIndex < steps.length - 1) {
			const newStepIndex = currentStepIndex + 1;

			setCurrentStepIndex(newStepIndex);

			localStorage.setItem(LocalStorageKeys.Step, newStepIndex.toString());
		} else {
			updateUserAgreements({
				acceptedTermsAndConditionsVersion: isChanged(Steps.TermsAndConditions),
				acceptedPrivacyPolicyVersion: isChanged(Steps.PrivacyPolicy),
				dataSharing: isChanged(Steps.DataSharing),
			}).then(() => {
				localStorage.removeItem(LocalStorageKeys.Step);
				localStorage.removeItem(LocalStorageKeys.State);

				onClose();
			});
		}
	};

	const handleFullscreenChange = useCallback(() => {
		setFullscreen(Boolean(document.fullscreenElement));
	}, []);

	useEffect(() => {
		document.addEventListener('fullscreenchange', handleFullscreenChange);

		return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
	}, []);

	useEffect(() => {
		const storedStep = localStorage.getItem(LocalStorageKeys.Step);
		const storedCheckboxes = localStorage.getItem(LocalStorageKeys.State);

		if (storedStep && !storedCheckboxes) {
			localStorage.removeItem(LocalStorageKeys.Step);
			return;
		}

		if (!storedCheckboxes) return;

		const parsedState = JSON.parse(storedCheckboxes) as CheckboxState;
		const stepIndex = Number(storedStep);

		// If there is any step that has `true` values while previous steps are not fully accepted → invalid state
		const hasInvalidTrue = steps.some((_, i) => {
			const prevSteps = steps.slice(0, i);
			const current = parsedState[steps[i].title] ?? [];

			const allPrevComplete = prevSteps.every((s) => (parsedState[s.title] ?? []).every(Boolean));
			const hasTrue = current.some(Boolean);

			return hasTrue && !allPrevComplete;
		});

		// If current step > 0 but previous steps are not fully accepted → invalid step position
		const prevIncomplete =
			stepIndex > 0 && steps.slice(0, stepIndex).some((s) => !(parsedState[s.title] ?? []).every(Boolean));

		const needReset = hasInvalidTrue || prevIncomplete;

		if (needReset) {
			localStorage.removeItem(LocalStorageKeys.Step);
			localStorage.removeItem(LocalStorageKeys.State);
			return;
		}

		setCheckboxState(parsedState);
		setCurrentStepIndex(Number(storedStep));
	}, [steps]);

	useEffect(() => {
		const onStorage = (event: StorageEvent) => {
			if (event.key === LocalStorageKeys.State) {
				const newState = event.newValue ? JSON.parse(event.newValue) : initialCheckboxState;

				setCheckboxState(newState);
			} else if (event.key === LocalStorageKeys.Step) {
				setCurrentStepIndex(event.newValue ? Number(event.newValue) : 0);
			}
		};

		window.addEventListener('storage', onStorage);

		return () => window.removeEventListener('storage', onStorage);
	}, [initialCheckboxState]);

	return {
		isLoading,
		isFullscreen,
		steps,
		sizeText,
		currentStep,
		isFirstAcceptance,
		checkboxState,
		handleCheckboxChange,
		nextStep,
		setCurrentStepIndex,
		isAllTrue,
	};
};
